import { supabase } from './supabase.ts';
import { migrateTokens } from './tokenMigrate.ts';

// Mirror of the server normalizer (netlify/functions/_lib/colors.ts) so the
// editor never displays a corrupted multi-'#' value even if old stored tokens
// still carry one. Idempotent.
function collapseLeadingHashes(s: string): string {
  const m = s.match(/^#+([0-9a-fA-F]{3,8})$/);
  return m ? `#${m[1]}` : s;
}
function normalizeColors(value: unknown): unknown {
  if (typeof value === 'string') return collapseLeadingHashes(value);
  if (Array.isArray(value)) return value.map(normalizeColors);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = normalizeColors(v);
    }
    return out;
  }
  return value;
}

/**
 * The client's CURRENT tokens — those from the most recent build that actually
 * pushed (commit_sha set), i.e. what's on `app/<slug>`. Used to pre-populate the
 * token editor so edits build on real current state, not the hardcoded sample.
 * Returns null if the client has no pushed build yet (caller falls back to the
 * sample). Old-shape stored tokens are migrated to the current TokenSchema shape
 * so the editor always produces a valid build input. RLS scopes readability.
 */
export async function loadCurrentTokens(clientId: string): Promise<unknown | null> {
  const { data } = await supabase
    .from('builds')
    .select('tokens')
    .eq('client_id', clientId)
    .not('commit_sha', 'is', null)
    .not('tokens', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data?.tokens ? migrateTokens(normalizeColors(data.tokens)) : null;
}

const RECENT_MS = 30 * 60 * 1000;

/** A build recent enough that its deploy is plausibly still in flight. */
export function isRecent(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < RECENT_MS;
}
